import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { EMPTY, Observable } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { IManualMatchingStatus } from './manual-cta-result-matching-type.interface';
import { ManualMatchingStatusService } from './manual-cta-result-matching-type.service';

interface IManualMatchingStatusState {
  error: string | null;
  loading: boolean;
  manualMatchingStatus: IManualMatchingStatus | null;
}

const initialState: IManualMatchingStatusState = {
  error: null,
  loading: false,
  manualMatchingStatus: null,
};

@Injectable()
export class ManualMatchingStatusStore extends ComponentStore<IManualMatchingStatusState> {
  public readonly manualMatchingStatus$ = this.selectSignal((s) => s.manualMatchingStatus);
  public readonly loading$ = this.selectSignal((s) => s.loading);
  public readonly error$ = this.selectSignal((s) => s.error);

  /** Resets the per-component result back to null so effectiveManualMatchingStatus defers to the list-store input. */
  public readonly resetStatus = this.updater(() => ({ ...initialState }));

  public readonly createManualMatchingStatus = this.effect((trigger$: Observable<IManualMatchingStatus>) =>
    trigger$.pipe(
      tap(() => this.patchState({ error: null, loading: true })),
      switchMap((payload) => {
        const source$ = this.service.createManualMatchingStatus(payload);

        return source$.pipe(
          tap((result: IManualMatchingStatus) => {
            this.patchState({ error: null, loading: false, manualMatchingStatus: result });
          }),
          catchError(() => {
            this.patchState({ error: 'Failed to create manual matching status', loading: false });

            return EMPTY;
          }),
        );
      }),
    ),
  );

  public readonly updateManualMatchingStatus = this.effect(
    (params$: Observable<IManualMatchingStatus & { id: string }>) =>
      params$.pipe(
        tap(() => this.patchState({ error: null, loading: true })),
        switchMap((params) => {
          const source$ = this.service.updateManualMatchingStatus(params.id, params);

          return source$.pipe(
            tap((result: IManualMatchingStatus) => {
              this.patchState({ error: null, loading: false, manualMatchingStatus: result });
            }),
            catchError(() => {
              this.patchState({ error: 'Failed to update manual matching status', loading: false });

              return EMPTY;
            }),
          );
        }),
      ),
  );

  constructor(private readonly service: ManualMatchingStatusService) {
    super(initialState);
  }
}
